import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar';
import TopNav from '../../components/TopNav';
import { adminService } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { useConfirm } from '../../contexts/ConfirmContext';
import { Card, Badge, Button, Modal, Input, Select, Textarea, Field, EmptyState, Spinner, Pagination } from '../../components/ui';

const EMPTY_FORM = { name: '', subscription_type: 'monthly', duration_days: 30, price: '', description: '', is_active: true };
const PAGE_SIZE = 20;

function AdminSubscriptions() {
  const { addNotification } = useNotification();
  const confirm = useConfirm();
  const { t } = useTranslation();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);

  const fetchSubs = () => {
    setLoading(true);
    adminService.getSubscriptions({ page, page_size: PAGE_SIZE })
      .then((res) => {
        const data = res.data;
        setSubs(Array.isArray(data) ? data : data.results || []);
        setCount(Array.isArray(data) ? data.length : data.count || 0);
      })
      .catch(() => addNotification(t('Failed to load subscription plans'), 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(fetchSubs, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setModalOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...s }); setModalOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const res = await adminService.updateSubscription(editing.id, form);
        setSubs((prev) => prev.map((s) => (s.id === editing.id ? res.data : s)));
      } else {
        const res = await adminService.createSubscription(form);
        setSubs((prev) => [...prev, res.data]);
        setCount((current) => current + 1);
      }
      addNotification(t('Subscription plan saved'), 'success');
      setModalOpen(false);
    } catch (e) {
      addNotification(getApiErrorMessage(e, t, 'Failed to save plan'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!(await confirm(t('Delete plan "{{name}}"?', { name: s.name })))) return;
    try {
      await adminService.deleteSubscription(s.id);
      setSubs((prev) => prev.filter((x) => x.id !== s.id));
      setCount((current) => Math.max(0, current - 1));
      addNotification(t('Plan deleted'), 'success');
    } catch (e) {
      addNotification(t('Failed to delete plan (it may be in use by enrollments)'), 'error');
    }
  };

  const typeLabels = { monthly: t('Monthly'), term: t('Term'), yearly: t('Yearly') };

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Subscription Plans')} subtitle={t('Manage pricing plans students can enroll with')} />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="flex justify-end mb-4">
            <Button onClick={openCreate}>+ {t('New Plan')}</Button>
          </div>

          {loading ? (
            <div className="p-12 flex justify-center"><Spinner /></div>
          ) : subs.length === 0 ? (
            <Card><EmptyState title={t('No subscription plans yet')} description={t('Create one to let students enroll.')} action={<Button onClick={openCreate}>+ {t('New Plan')}</Button>} /></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {subs.map((s) => (
                <Card key={s.id}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">{s.name}</p>
                      <p className="text-xs text-gray-500">{typeLabels[s.subscription_type] || s.subscription_type} · {t('{{count}} days', { count: s.duration_days })}</p>
                    </div>
                    <Badge tone={s.is_active ? 'green' : 'gray'}>{s.is_active ? t('Active') : t('Inactive')}</Badge>
                  </div>
                  <p className="text-2xl font-bold text-gray-800 mt-3">${s.price}</p>
                  {s.description && <p className="text-sm text-gray-500 mt-2">{s.description}</p>}
                  <div className="flex gap-2 mt-4">
                    <Button size="sm" variant="secondary" onClick={() => openEdit(s)}>{t('Edit')}</Button>
                    <Button size="sm" variant="danger" onClick={() => remove(s)}>{t('Delete')}</Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
          <Pagination page={page} count={count} pageSize={PAGE_SIZE} onPageChange={setPage} className="mt-4 rounded-xl border bg-white" />
        </main>
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? t('Edit Plan') : t('New Plan')}
        footer={<>
          <Button variant="secondary" onClick={() => setModalOpen(false)}>{t('Cancel')}</Button>
          <Button loading={saving} onClick={save}>{t('Save')}</Button>
        </>}
      >
        <div className="space-y-4">
          <Field label={t('Name')} required>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t('Type')} required>
              <Select value={form.subscription_type} onChange={(e) => setForm({ ...form, subscription_type: e.target.value })}>
                <option value="monthly">{t('Monthly')}</option>
                <option value="term">{t('Term')}</option>
                <option value="yearly">{t('Yearly')}</option>
              </Select>
            </Field>
            <Field label={t('Duration (days)')} required>
              <Input type="number" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} />
            </Field>
          </div>
          <Field label={t('Price ($)')} required>
            <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
          </Field>
          <Field label={t('Description')}>
            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            {t('Active (visible to students)')}
          </label>
        </div>
      </Modal>
    </div>
  );
}

export default AdminSubscriptions;
